import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '../lib/api/client';
import type { LeituraDaSessao, SessaoDeCaptura } from '../lib/api/types';
import { PageHeader } from '../components/ui/PageHeader';
import { Carregando, Erro, Vazio } from '../components/ui/Estados';
import { emDataHora, hashCurto } from '../lib/format/datetime';

/**
 * As sessões (A15) e o detalhe com o estado de ancoragem (A16).
 *
 * <h3>O que a empresa vê é a sessão de CAPTURA</h3>
 * O operador abre uma sessão de campo; o servidor separa por cliente sozinho (ADR-043). A
 * empresa enxerga a fatia daquele turno que produziu itens dela — nunca as leituras das
 * outras empresas do mesmo turno, e nem a contagem delas.
 *
 * <h3>"Ancorado" aqui também tem três estados</h3>
 * Fechada, raiz calculada, e raiz publicada. A tela não junta os dois últimos: enquanto a
 * rede for simulada, dizer "ancorado" seria prometer uma prova que ninguém consegue conferir
 * (ADR-065).
 */
export function SessoesPage() {
  const [aberta, setAberta] = useState<string | null>(null);

  const sessoes = useQuery({
    queryKey: ['sessoes'],
    queryFn: () => api.get<SessaoDeCaptura[]>('/console/sessions'),
  });

  return (
    <>
      <PageHeader
        titulo="Sessões"
        subtitulo="Cada turno de leitura da sua empresa, com o estado da prova."
      />
      <div className="wrap">
        {sessoes.isPending && <Carregando o="as sessões" />}
        {sessoes.isError && <Erro erro={sessoes.error} />}
        {sessoes.data && sessoes.data.length === 0 && (
          <Vazio
            titulo="Nenhuma sessão ainda"
            detalhe="Uma sessão nasce quando um operador em campo faz a primeira leitura de um item seu."
          />
        )}

        {sessoes.data && sessoes.data.length > 0 && (
          <div className="card tbl">
            <table className="t">
              <thead>
                <tr>
                  <th>Checkpoint</th>
                  <th>Elo</th>
                  <th>Aparelho</th>
                  <th>Aberta</th>
                  <th>Leituras</th>
                  <th>Prova</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {sessoes.data.map((s) => (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 600 }}>{s.checkpoint}</td>
                    <td>{s.unidade}</td>
                    {/* Responde "de qual aparelho veio isto?" sem abrir o banco (ADR-129). */}
                    <td className="t-cap">{s.nomeDoAparelho ?? '—'}</td>
                    <td className="t-cap">{emDataHora(s.abertaEm)}</td>
                    <td className="num">{s.folhas}</td>
                    <td>
                      <EstadoDaProva s={s} />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="btn btn-ghost"
                        onClick={() => setAberta(aberta === s.id ? null : s.id)}
                      >
                        {aberta === s.id ? 'Fechar' : 'Leituras'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {aberta && <Leituras sessionId={aberta} />}
      </div>
    </>
  );
}

function EstadoDaProva({ s }: { s: SessaoDeCaptura }) {
  if (s.status === 'OPEN') {
    return <span className="chip chip-warn">aberta</span>;
  }
  if (!s.raiz) {
    return <span className="chip chip-neutral">fechada, sem raiz</span>;
  }
  if (!s.publicada) {
    return (
      <span className="chip chip-navy" title={`raiz ${s.raiz}`}>
        raiz calculada · {hashCurto(s.raiz)}
      </span>
    );
  }
  return (
    <span className="chip chip-teal" title={`raiz ${s.raiz}`}>
      publicada em {s.rede}
    </span>
  );
}

function Leituras({ sessionId }: { sessionId: string }) {
  const leituras = useQuery({
    queryKey: ['sessao', sessionId, 'leituras'],
    queryFn: () => api.get<LeituraDaSessao[]>(`/console/sessions/${sessionId}/readings`),
  });

  return (
    <div className="card cd" style={{ marginTop: 16 }}>
      <div className="cd2-t">Leituras desta sessão</div>
      {leituras.isPending && <Carregando />}
      {leituras.isError && <Erro erro={leituras.error} />}
      {leituras.data && leituras.data.length === 0 && (
        <div className="t-small">Nenhuma leitura sua nesta sessão.</div>
      )}
      {leituras.data && leituras.data.length > 0 && (
        <table className="t">
          <thead>
            <tr>
              <th>Código</th>
              <th>Produto</th>
              <th>Quando</th>
              <th>Garantia</th>
            </tr>
          </thead>
          <tbody>
            {leituras.data.map((l) => (
              <tr key={l.eventId}>
                <td className="mono-s">{l.serial}</td>
                <td>{l.produto}</td>
                <td className="t-cap">{emDataHora(l.quando)}</td>
                <td>
                  {/* L3 é L2 com o lacre fechado (ADR-123) — e pintá-lo de cinza o faria parecer menos. */}
                  <span className={l.nivel === 'L1' ? 'chip chip-neutral' : 'chip chip-teal'}>
                    {l.nivel}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
